import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { AppRole, CreateAppRole, EditAppRole, UpdateRolePermissions } from '../../models/roles/app-role.model';
import { AppModule } from '../../models/roles/app-module.model';
import { RoleFeature } from '../../models/user-profile/role-feature.model';

@Injectable({
    providedIn: 'root',
})
export class RoleService {
    constructor(private http: HttpClient) {}

    // ── Roles ──────────────────────────────────────────────────────────────

    getRoles(): Observable<{ roles: AppRole[] }> {
        return this.http.get<{ roles: AppRole[] }>(
            `${environment.roles.root}`,
            { withCredentials: true }
        );
    }

    getRoleByGuid(roleGuid: string): Observable<{ role: AppRole | null }> {
        return this.http.get<{ role: AppRole | null }>(
            `${environment.roles.root}/${roleGuid}`,
            { withCredentials: true }
        );
    }

    createRole(payload: CreateAppRole): Observable<{ role: AppRole }> {
        return this.http.post<{ role: AppRole }>(
            `${environment.roles.root}`,
            payload,
            { withCredentials: true }
        );
    }

    editRole(roleGuid: string, payload: EditAppRole): Observable<{ role: AppRole }> {
        return this.http.put<{ role: AppRole }>(
            `${environment.roles.root}/${roleGuid}`,
            payload,
            { withCredentials: true }
        );
    }

    deleteRole(roleGuid: string): Observable<void> {
        return this.http.delete<void>(
            `${environment.roles.root}/${roleGuid}`,
            { withCredentials: true }
        );
    }

    // ── Role Permissions ───────────────────────────────────────────────────

    getRoleFeatures(roleGuid: string): Observable<{ roleFeatures: RoleFeature[] }> {
        return this.http.get<{ roleFeatures: RoleFeature[] }>(
            `${environment.roles.root}/${roleGuid}/features`,
            { withCredentials: true }
        );
    }

    updateRoleFeatures(roleGuid: string, permissions: UpdateRolePermissions[]): Observable<{ success: boolean }> {
        return this.http.put<{ success: boolean }>(
            `${environment.roles.root}/${roleGuid}/features`,
            permissions,
            { withCredentials: true }
        );
    }

    getModules(): Observable<{ modules: AppModule[] }> {
        return this.http.get<{ modules: AppModule[] }>(
            `${environment.roles.modules}`,
            { withCredentials: true }
        );
    }

    // ── User Roles ─────────────────────────────────────────────────────────

    getUserRole(userProfileGuid: string): Observable<{ role: AppRole | null; roles?: AppRole[] }> {
        return this.http.get<{ role: AppRole | null; roles?: AppRole[] }>(
            `${environment.roles.userRoles}/${userProfileGuid}`,
            { withCredentials: true }
        );
    }

    assignUserRole(userProfileGuid: string, roleGuid: string): Observable<{ success: boolean }> {
        return this.http.post<{ success: boolean }>(
            `${environment.roles.userRoles}/${userProfileGuid}/assign/${roleGuid}`,
            {},
            { withCredentials: true }
        );
    }

    removeUserRole(userProfileGuid: string, roleGuid: string): Observable<{ success: boolean }> {
        return this.http.delete<{ success: boolean }>(
            `${environment.roles.userRoles}/${userProfileGuid}/roles/${roleGuid}`,
            { withCredentials: true }
        );
    }
}
